/**
 * Shared Alpine factory for horizontal scroll-snap carousels.
 *
 * Exposed two ways so callers can choose the right one:
 *
 *   1. `Alpine.data('scrollCarousel', ...)` — for static carousels where
 *      the config is plain data (one or two selectors, no callbacks).
 *      Server-rendered tracks bind immediately.
 *
 *        x-data="scrollCarousel({ trackSelector: '[data-track]' })"
 *
 *   2. `window.theme.scrollCarousel(config)` — the raw factory function,
 *      for sections that need to bake in a loader or other behaviour.
 *      Wrap it in a section-specific Alpine.data registration inside
 *      that section's `{% javascript %}` block so the `x-data` attribute
 *      stays a short bare name (no inline logic in HTML attributes).
 *
 *        // inside the section's {% javascript %} block
 *        Alpine.data('productRecommendationsCarousel', () =>
 *          window.theme.scrollCarousel({ trackSelector: '...', loader: async () => { ... } })
 *        );
 *
 * Behaviour:
 *   - Tracks `atStart` / `atEnd` reactively so arrow controls can bind to
 *     `:disabled="atStart"` / `:disabled="atEnd"`.
 *   - Step a page worth via `step(direction)` (direction is -1 or 1).
 *   - Scroll listener is rAF-throttled.
 *
 * Optional config:
 *   - `loader`: async callback returning the inner HTML to splat into the
 *     track. If omitted, the carousel is static and the track is assumed
 *     server-rendered. If the loader resolves to a falsy value (or throws),
 *     the carousel stays collapsed; nothing is bound and no loaded
 *     attribute is set.
 *   - `loadedAttribute`: when set, the factory toggles this attribute on
 *     `$root` once the track is populated. Used by callers that gate
 *     section visibility on the loaded state.
 *
 * Idempotent: only registers itself once per page even if the script is
 * pulled in by multiple sections.
 */
(function () {
  if (window.__scrollCarouselRegistered) return;
  window.__scrollCarouselRegistered = true;

  function scrollCarouselFactory(config) {
    const settings = config || {};
    const trackSelector = settings.trackSelector;
    const loader = typeof settings.loader === 'function' ? settings.loader : null;
    const loadedAttribute = settings.loadedAttribute || null;

    return {
      atStart: true,
      atEnd: false,
      _rafId: null,
      _bound: false,

      init() {
        if (!trackSelector) return;
        const track = this.$root.querySelector(trackSelector);
        if (!track) return;

        // Server-rendered content: bind immediately.
        if (track.children.length > 0) {
          this._markLoaded();
          this._bindTrack(track);
          this.$nextTick(() => this.measure());
          return;
        }

        // No loader and no server-rendered content: nothing to do.
        if (!loader) return;

        Promise.resolve()
          .then(() => loader.call(this, { trackSelector }))
          .then((html) => {
            if (!html) return;
            track.innerHTML = html;
            if (track.children.length === 0) return;
            this._markLoaded();
            this._bindTrack(track);
            this.$nextTick(() => this.measure());
          })
          .catch(() => {
            /* Stay collapsed on failure. */
          });
      },

      _markLoaded() {
        if (!loadedAttribute) return;
        this.$root.setAttribute(loadedAttribute, '');
      },

      _bindTrack(track) {
        if (this._bound) return;
        track.addEventListener('scroll', () => this.schedule(), { passive: true });
        this._bound = true;
      },

      measure() {
        const t = this.$root.querySelector(trackSelector);
        if (!t) return;
        this.atStart = t.scrollLeft <= 1;
        this.atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 1;
      },

      schedule() {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this.measure();
        });
      },

      step(direction) {
        const t = this.$root.querySelector(trackSelector);
        if (!t) return;
        t.scrollBy({ left: direction * t.clientWidth * 0.85, behavior: 'smooth' });
      },
    };
  }

  window.theme = window.theme || {};
  window.theme.scrollCarousel = scrollCarouselFactory;

  document.addEventListener('alpine:init', () => {
    window.Alpine.data('scrollCarousel', scrollCarouselFactory);
  });
})();
